import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import type { FilterChip, Lookups } from "../../types";
import { defaultIntentSlice, type IntentSlice } from "./slice";
import { getIntentCadenceLabel } from "./constants";
import { getTopicLabel } from "./hooks";

/**
 * Lookups for Intent filter chips.
 */
export type IntentLookups = Lookups & {
  topics?: Array<{ id: string; name: string }>;
  buyersJourneys?: Array<{ id: string; name: string }>;
  contextTypes?: Array<{ id: string; name: string }>;
  countries?: Array<{ code: string; name: string }>;
  regions?: Array<{ id: string; label: string }>;
  states?: Array<{ id: string; name: string }>;
};

/**
 * Resolves IDs to names using lookups or cache.
 */
function resolveTopicNames(
  topicIds: string[],
  topicsCache: IntentSlice["topicsCache"],
  lookups?: IntentLookups
): string[] {
  const cacheById = new Map(topicsCache.map((t) => [t.id, t.displayName]));
  const lookupById = new Map(
    (lookups?.topics ?? []).map((t) => [t.id, t.name])
  );

  return topicIds.map((id) => {
    const cached = cacheById.get(id);
    if (cached) return cached;

    const lookup = lookupById.get(id);
    if (lookup) return lookup;

    return `Topic ${id.slice(0, 8)}...`;
  });
}

function resolveNames<T extends { id: string; name: string }>(
  ids: string[],
  lookupItems: T[] | undefined
): string[] {
  const lookupById = new Map(
    (lookupItems ?? []).map((item) => [item.id, item.name])
  );
  return ids.map((id) => lookupById.get(id) ?? id);
}

/**
 * Formats location for display in tooltip.
 */
function formatLocationSummary(
  location: IntentSlice["location"],
  lookups?: IntentLookups
): string | null {
  const parts: string[] = [];
  const regionById = new Map((lookups?.regions ?? []).map((r) => [r.id, r]));
  const countryByCode = new Map(
    (lookups?.countries ?? []).map((c) => [c.code, c])
  );
  const stateById = new Map((lookups?.states ?? []).map((s) => [s.id, s]));

  const excludedNames = location.excludedCountryAlpha2s.map((code) => {
    const country = countryByCode.get(code);
    return country?.name ?? code;
  });
  const excludedText =
    excludedNames.length <= 2
      ? excludedNames.join(", ")
      : `${excludedNames.length} countries`;

  if (location.approach === "regions" && location.regionNames.length > 0) {
    const regionLabels = location.regionNames.map((id) => {
      const region = regionById.get(id);
      return region?.label ?? id;
    });
    parts.push(regionLabels.join(", "));

    if (location.excludedCountryAlpha2s.length > 0) {
      parts.push(`excl. ${excludedText}`);
    }
  }

  if (location.approach === "countries" && location.countryAlpha2s.length > 0) {
    const countryNames = location.countryAlpha2s.map((code) => {
      const country = countryByCode.get(code);
      return country?.name ?? code;
    });
    const countryText =
      countryNames.length <= 3
        ? countryNames.join(", ")
        : `${countryNames.length} countries`;
    parts.push(countryText);

    if (location.excludedCountryAlpha2s.length > 0) {
      parts.push(`excl. ${excludedText}`);
    }
  }

  if (location.approach === "states" && location.stateIds.length > 0) {
    const stateNames = location.stateIds.map((id) => {
      const state = stateById.get(id);
      return state?.name ?? id;
    });
    const stateText =
      stateNames.length <= 3
        ? stateNames.join(", ")
        : `${stateNames.length} states (US)`;
    parts.push(stateText);
  }

  // Edge case: only exclusions set (possible in UI before selecting any regions/countries)
  if (parts.length === 0 && location.excludedCountryAlpha2s.length > 0) {
    parts.push(`excl. ${excludedText}`);
  }

  if (parts.length === 0) return null;
  return parts.join(" ");
}

/**
 * Checks if location filter is active.
 */
function hasActiveLocation(location: IntentSlice["location"]): boolean {
  return (
    location.countryAlpha2s.length > 0 ||
    location.excludedCountryAlpha2s.length > 0 ||
    location.regionNames.length > 0 ||
    location.stateIds.length > 0
  );
}

/**
 * Counts the number of additional filters (beyond topics).
 */
function countAdditionalFilters(slice: IntentSlice): number {
  let count = 0;
  if (slice.signalScore === "MEDIUM") count += 1;
  if (slice.buyersJourneyIds.length > 0) count += 1;
  if (slice.contextTypeIds.length > 0) count += 1;
  if (slice.numberOfCadences !== 4) count += 1;
  if (hasActiveLocation(slice.location)) count += 1;
  return count;
}

type AdditionalFilter = {
  id: string;
  icon: string;
  label: string;
  value: string;
  onRemove: () => void;
};

/**
 * Builds the list of additional filters for the tooltip.
 */
function buildAdditionalFilters(
  slice: IntentSlice,
  setSlice: (slice: IntentSlice) => void,
  lookups?: IntentLookups
): AdditionalFilter[] {
  const filters: AdditionalFilter[] = [];

  if (slice.signalScore === "MEDIUM") {
    filters.push({
      id: "signal",
      icon: "⚡",
      label: "Signal",
      value: "Medium+",
      onRemove: () => setSlice({ ...slice, signalScore: "HIGH" }),
    });
  }

  if (slice.buyersJourneyIds.length > 0) {
    const names = resolveNames(slice.buyersJourneyIds, lookups?.buyersJourneys);
    filters.push({
      id: "journey",
      icon: "🎯",
      label: "Journey",
      value: names.join(", "),
      onRemove: () => setSlice({ ...slice, buyersJourneyIds: [] }),
    });
  }

  if (slice.contextTypeIds.length > 0) {
    const names = resolveNames(slice.contextTypeIds, lookups?.contextTypes);
    filters.push({
      id: "context",
      icon: "🔬",
      label: "Context",
      value: names.join(", "),
      onRemove: () => setSlice({ ...slice, contextTypeIds: [] }),
    });
  }

  if (slice.numberOfCadences !== 4) {
    const dateLabel = getIntentCadenceLabel(slice.numberOfCadences);
    filters.push({
      id: "recency",
      icon: "📅",
      label: "Recency",
      value: dateLabel,
      onRemove: () => setSlice({ ...slice, numberOfCadences: 4 }),
    });
  }

  const locationSummary = formatLocationSummary(slice.location, lookups);
  if (locationSummary) {
    filters.push({
      id: "location",
      icon: "📍",
      label: "Location",
      value: locationSummary,
      onRemove: () =>
        setSlice({
          ...slice,
          location: { ...defaultIntentSlice.location },
        }),
    });
  }

  return filters;
}

/**
 * Creates the rich tooltip content for the consolidated Intent chip.
 */
function createIntentTooltip(
  slice: IntentSlice,
  topicNames: string[],
  additionalFilters: AdditionalFilter[]
): React.ReactNode {
  const inclusionText =
    slice.topicsInclusionMethod === "ALL_PRESENT" ? "Match ALL" : "Match ANY";

  const maxTopicsToShow = 10;
  const visibleTopics = topicNames.slice(0, maxTopicsToShow);
  const remainingCount = topicNames.length - maxTopicsToShow;

  const hasCollection = Boolean(slice.collectionId);

  return (
    <Box sx={{ minWidth: 280, maxWidth: 360 }}>
      {/* Collection Header (if applicable) */}
      {hasCollection && (
        <Box
          sx={{
            mb: 1.5,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            📁 Collection: {slice.collectionName ?? "Loading..."}
          </Typography>
        </Box>
      )}

      {/* Topics Section */}
      <Box sx={{ mb: additionalFilters.length > 0 ? 1.5 : 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.75,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            📋 Topics ({topicNames.length})
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              bgcolor: "grey.100",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: 11,
            }}
          >
            {inclusionText}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 1,
            p: 1,
            maxHeight: 140,
            overflow: "auto",
          }}
        >
          {visibleTopics.map((name, idx) => (
            <Typography
              key={idx}
              variant="body2"
              sx={{
                fontSize: 12,
                color: "text.secondary",
                py: 0.25,
                lineHeight: 1.4,
              }}
            >
              • {name}
            </Typography>
          ))}
          {remainingCount > 0 && (
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                color: "text.disabled",
                fontStyle: "italic",
                py: 0.25,
                mt: 0.5,
              }}
            >
              ... and {remainingCount} more
            </Typography>
          )}
        </Box>
      </Box>

      {/* Additional Filters Section */}
      {additionalFilters.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
            >
              ⚙️ Additional Filters
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {additionalFilters.map((filter) => (
                <Chip
                  key={filter.id}
                  label={`${filter.icon} ${filter.label}: ${filter.value}`}
                  size="small"
                  onDelete={filter.onRemove}
                  sx={{
                    height: 24,
                    fontSize: 11,
                    bgcolor: "grey.100",
                    color: "text.primary",
                    "& .MuiChip-deleteIcon": {
                      fontSize: 14,
                      color: "text.secondary",
                      "&:hover": {
                        color: "error.main",
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Generates a single consolidated chip for the Intent filter.
 * Shows "Intent: [CollectionName] (+N filters)" when a collection is active,
 * otherwise "Intent: X topics (+N filters)".
 */
export function getChips(
  slice: IntentSlice,
  setSlice: (slice: IntentSlice) => void,
  lookups?: IntentLookups
): FilterChip[] {
  // Only show chip if topics are selected
  if (slice.topicIds.length === 0) {
    return [];
  }

  const topicNames = resolveTopicNames(
    slice.topicIds,
    slice.topicsCache,
    lookups
  );

  const additionalFiltersCount = countAdditionalFilters(slice);
  const additionalFilters = buildAdditionalFilters(slice, setSlice, lookups);

  // Build the label - use collection name if available
  let label: string;
  if (slice.collectionId) {
    label = `Intent: ${slice.collectionName ?? "Collection"}`;
  } else {
    const topicLabel = getTopicLabel(topicNames);
    label = `Intent: ${topicLabel}`;
  }

  // Append additional filters count if any
  if (additionalFiltersCount > 0) {
    const filterWord = additionalFiltersCount === 1 ? "filter" : "filters";
    label += ` (+${additionalFiltersCount} ${filterWord})`;
  }

  return [
    {
      id: "intent",
      label,
      filterKey: "intent" as const,
      onRemove: () => setSlice(defaultIntentSlice),
      tooltipContent: createIntentTooltip(slice, topicNames, additionalFilters),
    },
  ];
}

/**
 * Returns count for sidebar badge.
 * Counts topics as primary, plus 1 for each active secondary filter.
 */
export function getCount(slice: IntentSlice): number {
  let count = 0;

  if (slice.topicIds.length > 0) count += 1;
  if (slice.signalScore === "MEDIUM") count += 1;
  if (slice.buyersJourneyIds.length > 0) count += 1;
  if (slice.contextTypeIds.length > 0) count += 1;
  if (slice.numberOfCadences !== 4) count += 1;
  if (hasActiveLocation(slice.location)) count += 1;

  return count;
}
